import { useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Upload, Eye, EyeOff, Send } from "lucide-react";
import { BACKEND } from "./shared";

export function Photos({ data, reference, reload, canManage }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [uploading, setUploading] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [tab, setTab] = useState("internal");

  const photos = (data.photos || []).map(p => ({ ...p, visibility: p.visibility === "client" ? "client" : "internal" }));
  const counts = {
    internal: photos.filter(p => p.visibility === "internal").length,
    client: photos.filter(p => p.visibility === "client").length,
  };
  const shown = photos.filter(p => p.visibility === tab);

  const upload = async () => {
    if (!file) { toast.error("Choose a photo"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("caption", caption); fd.append("visibility", visibility);
      await api.post(`/admin/booking/${reference}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(visibility === "client" ? "Photo added — visible to the client" : "Photo added (internal only)");
      setFile(null); setCaption(""); setTab(visibility); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const del = async (id) => { try { await api.delete(`/admin/photo/${id}`); reload(); } catch { toast.error("Cannot delete"); } };
  const toggleVis = async (p) => {
    const next = p.visibility === "client" ? "internal" : "client";
    try { await api.patch(`/admin/photo/${p.id}`, { visibility: next }); toast.success(next === "client" ? "Now visible to the client" : "Set to internal only"); reload(); }
    catch { toast.error("Could not update"); }
  };
  const notify = async () => {
    setNotifying(true);
    try {
      const r = await api.post(`/admin/booking/${reference}/notify-photos`);
      toast.success(r.data.sent ? `Emailed ${r.data.count} photo${r.data.count === 1 ? "" : "s"} to ${r.data.to}` : "Logged, but the email could not be sent — check Settings → Connected email.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not notify the client"); }
    setNotifying(false);
  };

  return (
    <div data-testid="jobfile-photos">
      <div className="border border-[#E5E7EB] bg-white p-3 mb-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <label className="de-btn-secondary cursor-pointer inline-flex shrink-0 justify-center"><Upload size={16} /> {file ? file.name.slice(0, 16) : "Choose photo"}
          <input data-testid="photo-input" type="file" accept="image/*" className="hidden" onChange={e => setFile(e.target.files?.[0])} /></label>
        <input data-testid="photo-caption" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Caption (optional)" className="border border-[#E5E7EB] px-3 py-2 text-sm flex-1 w-full" />
        <select data-testid="photo-visibility" value={visibility} onChange={e => setVisibility(e.target.value)} className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white shrink-0">
          <option value="internal">Internal only</option>
          <option value="client">Client-visible</option>
        </select>
        <button data-testid="photo-upload" onClick={upload} disabled={uploading} className="de-btn-primary shrink-0 justify-center">{uploading ? "…" : "Upload"}</button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex gap-2">
          {[["internal", "Internal"], ["client", "Client-visible"]].map(([k, label]) => (
            <button key={k} data-testid={`photos-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${tab === k ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB] text-[#6B7280]"}`}>
              {label} <span className="ml-1 opacity-70">{counts[k]}</span>
            </button>
          ))}
        </div>
        {canManage && counts.client > 0 && (
          <button data-testid="photos-notify" onClick={notify} disabled={notifying}
            className="de-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60">
            <Send size={13} /> {notifying ? "Sending…" : "Notify client now"}
          </button>
        )}
      </div>
      {canManage && (
        <p className="text-[11px] text-[#9CA3AF] -mt-1 mb-3" data-testid="photos-autonotify-hint">
          Clients are emailed automatically when you publish client-visible photos (max once every 24h).
          {data.booking?.last_photo_notify_at ? ` Last notified ${new Date(data.booking.last_photo_notify_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}.` : " Not notified yet."}
          {" "}Use “Notify client now” to send immediately.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {shown.map(p => (
          <div key={p.id} data-testid={`photo-${p.id}`} className="border border-[#E5E7EB] bg-white overflow-hidden group relative">
            <img src={`${BACKEND}/api/admin/job-photo/${p.id}`} alt={p.caption} className="w-full h-32 object-cover" />
            <span className={`absolute top-1 left-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 ${p.visibility === "client" ? "bg-[#1F6B3B] text-white" : "bg-[#12263A] text-white"}`}>
              {p.visibility === "client" ? "Client" : "Internal"}
            </span>
            <div className="p-2 text-xs truncate">{p.caption || "—"}</div>
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100">
              <button data-testid={`photo-toggle-${p.id}`} onClick={() => toggleVis(p)} title={p.visibility === "client" ? "Make internal" : "Share with client"}
                className="bg-white/90 border text-[10px] font-bold uppercase px-1.5 py-1 hover:bg-[#1F6B3B] hover:text-white inline-flex items-center gap-1">
                {p.visibility === "client" ? <EyeOff size={11} /> : <Eye size={11} />}
              </button>
              <button data-testid={`photo-delete-${p.id}`} onClick={() => del(p.id)} className="bg-white/90 border text-[10px] font-bold uppercase px-2 py-1 hover:bg-[#C2401F] hover:text-white">Del</button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="col-span-full text-sm text-[#6B7280]">{tab === "client" ? "No client-visible photos yet. Upload one or switch an internal photo to client-visible to share build progress." : "No internal photos yet."}</div>}
      </div>
    </div>
  );
}
